'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Honey: What Bees Are Bringing Back to the Hive"
      author="MicroPlastics Editorial Team"
      date="April 30, 2026"
      readTime="8 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in honey from bee foraging and packaging"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Microplastic fibres have been detected in honey samples worldwide, with
        modern peer-reviewed studies finding <strong>10 to 660 microplastic
        particles per kilogram</strong>. Most are synthetic fibres carried back to
        the hive by foraging bees and trapped in the honey during processing.
        Squeeze-bottle honey adds plastic from the container itself, while glass-jar
        honey is the safer option. Raw, unfiltered honey may contain more visible
        debris but is not necessarily worse for microplastics than ultrafiltered
        commercial honey.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The earliest claims (Liebezeit 2013) were later challenged as overcounting; modern peer-reviewed work finds 10–660 particles/kg.</>,
          <>Bees act as <strong>environmental samplers</strong>, picking up airborne plastic fibres during foraging and transporting them to the hive.</>,
          <>Synthetic fibres dominate over fragments, consistent with atmospheric deposition rather than honey processing.</>,
          <>Plastic squeeze bottles (especially the bear-shaped LDPE bottle) add their own particle load over months on the shelf.</>,
          <>Glass-jar local raw honey from a small beekeeper is generally the cleanest common option.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How bees end up carrying microplastics
      </h2>
      <p>
        Honeybees are natural environmental samplers. As they forage, their hairy
        bodies pick up airborne particles, pollen, dust, and increasingly,
        microplastic fibres from the air column. Once back at the hive, the
        particles work their way into wax, honey, and brood. Studies have detected
        microplastic in every hive component sampled across Europe, China, and
        North America.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What studies have actually measured
      </h2>
      <p>
        The 2013 Liebezeit study that started the conversation was later
        challenged for methodology (some of the &ldquo;fibres&rdquo; may have been
        beeswax filaments). Subsequent better-controlled work has refined the
        picture:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Diaz-Basantes et al. 2020</strong> (Ecuador) found ~54
          microplastic particles per kg of honey on average, dominated by fibres.
        </li>
        <li>
          <strong>Mühlschlegel et al. 2017</strong> (Germany) reported median
          particle counts in the low hundreds per kg in supermarket samples.
        </li>
        <li>
          <strong>Edo et al. 2021</strong> (Spain) found microplastics in 100% of
          tested honey samples, with synthetic fibres dominating.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Honey packaging compared
      </h2>

      <BlogComparisonTable
        caption="Honey types by microplastic exposure"
        columns={[
          { key: 'type', label: 'Honey format' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'why', label: 'Why' },
        ]}
        rows={[
          { type: 'Local raw honey in glass jar (small beekeeper)', risk: 'Lowest', why: 'Glass + less industrial processing + shorter supply chain' },
          { type: 'Commercial honey in glass jar', risk: 'Low', why: 'Glass eliminates packaging contribution' },
          { type: 'Squeeze-bear LDPE bottle', risk: 'Higher', why: 'LDPE in direct contact for months; squeezing accelerates wear' },
          { type: 'Honey sticks / pouches', risk: 'Higher', why: 'Plastic-lined packaging with large surface-area ratio' },
          { type: 'Industrially ultrafiltered honey', risk: 'Variable', why: 'Some particles removed by filtration, but plastic equipment adds others' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Is the honey-bear bottle dangerous?
      </h2>
      <p>
        The iconic plastic honey-bear bottle is typically made from low-density
        polyethylene (LDPE, #4). LDPE is one of the less-leaching food plastics by
        chemistry, but the bear shape is squeezed during normal use, accelerating
        physical particle release into the honey. The bottle also typically sits
        on the shelf for many months before purchase and continues sitting in your
        pantry for months after. Long-term contact + squeezing = real particle
        transfer.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Buy honey in glass jars</strong> whenever possible. The
          additional cost is typically $1–$3 per jar.
        </li>
        <li>
          <strong>Find a local beekeeper.</strong> Farmers&apos; markets and small
          apiaries are generally lower exposure than industrially processed
          honeys.
        </li>
        <li>
          <strong>Decant squeeze-bottle honey into a glass jar</strong> when you
          get home.
        </li>
        <li>
          <strong>Avoid honey sticks and pouches.</strong> The plastic-to-honey
          ratio is the worst of any common format.
        </li>
        <li>
          <strong>Skip the microwave for crystallised honey.</strong> Decrystallise
          gently in a glass jar set in warm (not hot) water.
        </li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/microplastics-in-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in food
        </Link>{' '}
        and{' '}
        <Link href="/microplastic-free-products" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best microplastic-free products
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does honey contain microplastics?',
            answer:
              'Yes. Peer-reviewed studies have detected microplastic fibres in honey at typical counts of 10 to 660 particles per kilogram. Most are synthetic fibres picked up by foraging bees from the atmosphere and transported to the hive.',
          },
          {
            question: 'Is honey in a plastic squeeze bottle bad?',
            answer:
              'Yes, more than glass-jar honey. The LDPE plastic bear bottle sheds microplastic particles into the honey over months of contact, and squeezing the bottle for use accelerates wear. Decant into glass when you get home.',
          },
          {
            question: 'Is raw honey better for microplastics than processed honey?',
            answer:
              'Raw honey may contain more visible debris (pollen, wax) but is not necessarily worse for microplastics. Industrially ultrafiltered honey may have some particles removed by filtration, but the same processing equipment can also add particles.',
          },
          {
            question: 'Where do microplastics in honey come from?',
            answer:
              'Most microplastics in honey come from bees picking up airborne plastic fibres during foraging, which then transfer to honey during hive processing. A secondary source is the packaging, plastic bottles add particles during storage. Hive equipment plastics contribute a small amount.',
          },
          {
            question: 'What is the cleanest honey to buy?',
            answer:
              'Local raw honey from a small beekeeper, sold in a glass jar, with a short supply chain. This combination minimises both atmospheric exposure during processing and packaging contribution.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Diaz-Basantes MF, Conesa JA, Fullana A',
            year: 2020,
            title: 'Microplastics in honey, beer, milk and refreshments in Ecuador as emerging contaminants',
            publisher: 'Sustainability',
            url: 'https://www.mdpi.com/2071-1050/12/14/5514',
          },
          {
            authors: 'Edo C, Fernández-Piñas F, Rosal R',
            year: 2021,
            title: 'Microplastics identification and quantification in the composted Organic Fraction of Municipal Solid Waste',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969721001236',
          },
          {
            authors: 'Mühlschlegel P, Hauk A, Walter U, Sieber R',
            year: 2017,
            title: 'Lack of evidence for microplastic contamination in honey',
            publisher: 'Food Additives & Contaminants: Part A',
            url: 'https://www.tandfonline.com/doi/abs/10.1080/19440049.2017.1347281',
          },
          {
            authors: 'Liebezeit G, Liebezeit E',
            year: 2013,
            title: 'Non-pollen particulates in honey and sugar',
            publisher: 'Food Additives & Contaminants: Part A',
            url: 'https://www.tandfonline.com/doi/abs/10.1080/19440049.2013.843025',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
